import classNames from 'classnames'
import styles from 'sections/hero/Hero.module.scss'
import SplitSection from 'components/split-section/SplitSection'
import { PropsWithChildren, ReactNode } from 'react'
import { HeroProps } from 'sections/hero/types'
import StartSimulationCard from 'components/start-simulation-card/StartSimulationCard'
import { Heading, Video, Img } from '@boltenergy-be/design-system'
import { BackgroundColors } from 'types/layout'

const Title = ({ value, className }: { value: string | ReactNode; className?: string }) => {
  return (
    <Heading as="h1" variant="h2" className={classNames(styles.title, className)}>
      {value}
    </Heading>
  )
}

const Paragraph = ({ value, className }: { value: string | ReactNode; className?: string }) => {
  return <p className={className}>{value}</p>
}

const CtaGroup = ({ children }: { children: ReactNode }) => {
  return <div className={styles['hero-cta-group']}>{children}</div>
}

const Hero = ({
  absoluteBottomSvg,
  backgroundColor = BackgroundColors.SUBDUED,
  children,
  className,
  image,
  specificProductSimulation,
  video,
  withSimulationForm,
  color,
  id
}: PropsWithChildren<HeroProps>) => {
  return image ? (
    <SplitSection
      id={id || 'hero'}
      className={classNames(styles['split-hero'], styles[backgroundColor], { [styles['with-form']]: withSimulationForm }, className)}
      color={color}
    >
      <SplitSection.Left className={styles['hero-content']}>{children}</SplitSection.Left>
      <SplitSection.Right className={styles.right}>
        <figure className={styles.figure}>
          {video ? (
            <Video publicId={video} className={styles['hero-video']} />
          ) : (
            <Img
              fill
              priority
              publicId={image.publicId}
              alt={image.alt ?? ''}
              sizes="(min-width: 920px) 50vw, 100vw"
              className={image.customClass || ''}
              steps={1000}
            />
          )}
          {image.caption && (
            <figcaption>
              <strong>{image.caption.text}</strong>
              {image.caption.extra && <small>{image.caption.extra}</small>}
            </figcaption>
          )}
        </figure>
      </SplitSection.Right>

      {absoluteBottomSvg}

      {withSimulationForm && (
        <div className={classNames('container', styles['form-container'])}>
          <StartSimulationCard specificProduct={specificProductSimulation} />
        </div>
      )}
    </SplitSection>
  ) : (
    <section className={classNames(styles.hero, styles[backgroundColor], 'container', className)}>{children}</section>
  )
}

Hero.Title = Title
Hero.Paragraph = Paragraph
Hero.CtaGroup = CtaGroup

export default Hero
